export const cloneDashboardValue = (value) => {
  if (value instanceof Blob) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(cloneDashboardValue);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      acc[key] = cloneDashboardValue(nestedValue);
      return acc;
    }, {});
  }

  return value;
};

export const getElementViewportHeight = (element) => {
  if (!element) return 1;

  return Math.max(
    1,
    Math.round(element.getBoundingClientRect()?.height || 0),
    element.clientHeight || 0,
    element.offsetHeight || 0
  );
};

export const getCanvasBottomBufferPx = ({ viewportHeight, defaultHeight, rowUnit }) =>
  Math.max(
    Math.max(1, rowUnit),
    Math.min(defaultHeight, Math.round(viewportHeight * 0.15))
  );

export const normalizeRect = (rect) => {
  const left = Math.min(rect.left, rect.left + rect.width);
  const top = Math.min(rect.top, rect.top + rect.height);
  const width = Math.abs(rect.width);
  const height = Math.abs(rect.height);
  return { left, top, width, height };
};

export const rectsIntersect = (a, b) => (
  a.left < b.left + b.width &&
  a.left + a.width > b.left &&
  a.top < b.top + b.height &&
  a.top + a.height > b.top
);

export const sameRect = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    Math.abs((Number(a.left) || 0) - (Number(b.left) || 0)) < 0.5 &&
    Math.abs((Number(a.top) || 0) - (Number(b.top) || 0)) < 0.5 &&
    Math.abs((Number(a.width) || 0) - (Number(b.width) || 0)) < 0.5 &&
    Math.abs((Number(a.height) || 0) - (Number(b.height) || 0)) < 0.5
  );
};

export const sameIdList = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  return a.every((id, index) => String(id) === String(b[index]));
};

export const sameOffset = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    Math.abs((Number(a.x) || 0) - (Number(b.x) || 0)) < 0.5 &&
    Math.abs((Number(a.y) || 0) - (Number(b.y) || 0)) < 0.5
  );
};

export const getRectBounds = (rects) => {
  if (!Array.isArray(rects) || rects.length === 0) return null;
  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.left + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.top + rect.height));
  return { left, top, width: right - left, height: bottom - top };
};

export const setElementPixelStyle = (element, property, value, priority = '') => {
  if (!element || !Number.isFinite(value)) return;
  element.style.setProperty(property, `${Math.max(1, Math.round(value))}px`, priority);
};

export const toPxNumber = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const numericValue = Number(String(value).replace('px', '').trim());
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const formatPercentValue = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0%';
  const clamped = Math.max(0, numericValue);
  const rounded = Number(clamped.toFixed(4));
  return `${rounded}%`;
};

export const isStoredPercentageValue = (value) => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  if (!normalized.endsWith('%')) return false;
  const numericValue = Number(normalized.slice(0, -1));
  return Number.isFinite(numericValue);
};

export const hasStoredPercentageGeometry = (widget) => (
  isStoredPercentageValue(widget?.left)
  && isStoredPercentageValue(widget?.top)
  && isStoredPercentageValue(widget?.width)
  && isStoredPercentageValue(widget?.height)
);

export const parseStoredDimension = (value, total, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    const numericValue = Number(value.replace('%', '').trim());
    if (!Number.isFinite(numericValue) || total <= 0) {
      return fallback;
    }
    return (numericValue / 100) * total;
  }
  return toPxNumber(value, fallback);
};

export const parseStoredPosition = (value, total, origin = 0, fallback = origin) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    const numericValue = Number(value.replace('%', '').trim());
    if (!Number.isFinite(numericValue) || total <= 0) {
      return fallback;
    }
    return origin + ((numericValue / 100) * total);
  }
  return toPxNumber(value, fallback);
};

export const sameMeasuredSize = (prev, next) => {
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  return (
    Math.abs((Number(prev.width) || 0) - (Number(next.width) || 0)) < 1 &&
    Math.abs((Number(prev.height) || 0) - (Number(next.height) || 0)) < 1
  );
};

export const getNearestGridRect = ({
  rect,
  metrics,
  defaultDivWidth,
  defaultDivHeight,
  defaultWidth,
  defaultHeight,
  toPxNumber,
}) => {
  const gap = metrics?.gap || 0;
  const gridColumnSize = Math.max(1, defaultDivWidth + gap);
  const gridRowSize = Math.max(1, defaultDivHeight + gap);
  const minLeft = metrics?.padLeft || 0;
  const minTop = metrics?.padTop || 0;
  const maxWidth = Math.max(defaultDivWidth, (metrics?.width || 0) - minLeft - (metrics?.padRight || 0));
  const maxHeight = Math.max(defaultDivHeight, (metrics?.height || 0) - minTop - (metrics?.padBottom || 0));
  const rawWidth = Math.max(1, toPxNumber(rect?.width, defaultWidth));
  const rawHeight = Math.max(1, toPxNumber(rect?.height, defaultHeight));
  const cols = Math.max(1, Math.round((rawWidth + gap) / gridColumnSize));
  const rows = Math.max(1, Math.round((rawHeight + gap) / gridRowSize));
  const width = Math.min(maxWidth, (cols * defaultDivWidth) + (Math.max(0, cols - 1) * gap));
  const height = Math.min(maxHeight, (rows * defaultDivHeight) + (Math.max(0, rows - 1) * gap));
  const maxLeft = Math.max(minLeft, (metrics?.width || 0) - (metrics?.padRight || 0) - width);
  const maxTop = Math.max(minTop, (metrics?.height || 0) - (metrics?.padBottom || 0) - height);
  const left = minLeft + (
    Math.round((toPxNumber(rect?.left, minLeft) - minLeft) / gridColumnSize) * gridColumnSize
  );
  const top = minTop + (
    Math.round((toPxNumber(rect?.top, minTop) - minTop) / gridRowSize) * gridRowSize
  );

  return {
    left: Math.max(minLeft, Math.min(left, maxLeft)),
    top: Math.max(minTop, Math.min(top, maxTop)),
    width,
    height,
  };
};
